import { canPerformCapability, type CallerIdentity } from '@myplayer/rbac';
import type { OrganisationDocument } from '@myplayer/schema';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  getFirebaseClients,
  isFunctionsEmulatorOrLiveBackendConfigured,
  isLocalEmulatorMode,
} from './firebase.js';
import { slugifyWorkspaceName } from './workspaces-firestore.js';

export interface ActivateInvitedMembershipCallablePayload {
  readonly orgId?: string;
}

export interface ActivateInvitedMembershipCallableResult {
  readonly activated: boolean;
  readonly membershipIds: readonly string[];
  readonly status: 'ACTIVE' | 'NONE';
  readonly updatedAt: string;
  readonly auditId?: string;
}

export async function callActivateInvitedMembershipFunction(
  payload?: ActivateInvitedMembershipCallablePayload,
): Promise<ActivateInvitedMembershipCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      activated: false,
      membershipIds: [],
      status: 'NONE',
      updatedAt: new Date().toISOString(),
    };
  }
  const { functions } = getFirebaseClients();
  const res = await httpsCallable<
    ActivateInvitedMembershipCallablePayload | undefined,
    ActivateInvitedMembershipCallableResult
  >(
    functions,
    'activateInvitedMembership',
  )(payload);
  return res.data;
}

export interface UpdateOrganisationCallableFields {
  readonly name?: string;
  readonly slug?: string;
  readonly gcpCompanyPrefixes?: readonly string[];
  readonly verifiedByGs1Enabled?: boolean;
  readonly tier?: 'STANDARD' | 'ENTERPRISE';
  readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

export interface UpdateOrganisationCallablePayload extends UpdateOrganisationCallableFields {
  readonly orgId: string;
  readonly patch?: UpdateOrganisationCallableFields;
  readonly updates?: UpdateOrganisationCallableFields;
  readonly reason?: string;
}

export interface UpdateOrganisationCallableResult {
  readonly orgId: string;
  readonly organisation: OrganisationDocument;
  readonly updatedAt: string;
  readonly auditId: string;
}

export async function callUpdateOrganisationFunction(
  payload: UpdateOrganisationCallablePayload,
): Promise<UpdateOrganisationCallableResult> {
  const { functions } = getFirebaseClients();
  const res = await httpsCallable<
    UpdateOrganisationCallablePayload,
    UpdateOrganisationCallableResult
  >(
    functions,
    'updateOrganisation',
  )(payload);
  return res.data;
}

export function formatOrganisationName(rawName: string | undefined, orgId: string): string {
  const trimmed = rawName?.trim() ?? '';
  if (trimmed.length === 0) {
    return orgId === 'org_default' ? 'MyPlayer Organisation' : orgId;
  }
  return trimmed;
}

export function mapSnapshotDocToOrganisation(
  docId: string,
  data: Partial<OrganisationDocument> | undefined,
  fallbackOrgId?: string,
): OrganisationDocument {
  const orgId = data?.orgId ?? (docId || fallbackOrgId || 'org_default');
  const name = formatOrganisationName(data?.name, orgId);
  const slug =
    typeof data?.slug === 'string' && data.slug.trim().length > 0
      ? data.slug.trim()
      : slugifyWorkspaceName(name) || orgId.replace(/[^a-z0-9-]+/gi, '-').toLowerCase();
  const status =
    data?.status === 'SUSPENDED' || data?.status === 'ARCHIVED' ? data.status : 'ACTIVE';
  const tier = data?.tier === 'STANDARD' ? 'STANDARD' : 'ENTERPRISE';
  const gcpCompanyPrefixes = Array.isArray(data?.gcpCompanyPrefixes)
    ? data.gcpCompanyPrefixes.filter((p): p is string => typeof p === 'string')
    : [];
  const verifiedByGs1Enabled =
    typeof data?.verifiedByGs1Enabled === 'boolean' ? data.verifiedByGs1Enabled : false;
  return {
    orgId,
    name,
    slug,
    status,
    tier,
    gcpCompanyPrefixes,
    verifiedByGs1Enabled,
    createdAt: typeof data?.createdAt === 'string' ? data.createdAt : '',
    updatedAt: typeof data?.updatedAt === 'string' ? data.updatedAt : '',
    updatedBy: data?.updatedBy ?? '',
  };
}

export function validateOrganisationDraft(params: {
  readonly name: string;
  readonly slug: string;
  readonly prefixesInput: string;
}):
  | {
      readonly ok: true;
      readonly name: string;
      readonly slug: string;
      readonly prefixes: readonly string[];
    }
  | { readonly ok: false; readonly error: string } {
  const trimmedName = params.name.trim();
  if (trimmedName.length === 0) {
    return { ok: false, error: 'Please enter an organisation name.' };
  }
  if (trimmedName.length > 128) {
    return { ok: false, error: 'Organisation name must be 128 characters or fewer.' };
  }
  const normalizedSlug = slugifyWorkspaceName(params.slug.trim() || trimmedName);
  if (normalizedSlug.length === 0 || !/^[a-z0-9-]+$/.test(normalizedSlug)) {
    return {
      ok: false,
      error: 'URL slug must contain lowercase letters, numbers, or hyphens.',
    };
  }
  const rawEntries = params.prefixesInput
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  for (const entry of rawEntries) {
    if (!/^\d{4,12}$/.test(entry)) {
      return {
        ok: false,
        error:
          'Each GS1 company prefix must be 4 to 12 digits (for example, 9506000). Separate multiple prefixes with commas.',
      };
    }
  }
  return { ok: true, name: trimmedName, slug: normalizedSlug, prefixes: rawEntries };
}

export interface UpdateOrganisationInFirestoreParams {
  readonly db: Firestore;
  readonly caller: CallerIdentity;
  readonly orgId: string;
  readonly name: string;
  readonly slug: string;
  readonly gcpCompanyPrefixes: readonly string[];
  readonly verifiedByGs1Enabled: boolean;
  readonly tier?: 'STANDARD' | 'ENTERPRISE';
  readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

export async function updateOrganisationInFirestore(
  params: UpdateOrganisationInFirestoreParams,
): Promise<OrganisationDocument> {
  if (!canPerformCapability(params.caller, 'organisation:update', params.orgId)) {
    throw new Error(
      `Caller (${params.caller.role}) in org '${params.caller.orgId}' is not permitted to perform 'organisation:update' on org '${params.orgId}'.`,
    );
  }
  const isSuperAdmin = params.caller.role === 'SUPER_ADMIN';
  const nowIso = new Date().toISOString();

  if (isFunctionsEmulatorOrLiveBackendConfigured()) {
    const res = await callUpdateOrganisationFunction({
      orgId: params.orgId,
      name: params.name,
      slug: params.slug,
      gcpCompanyPrefixes: params.gcpCompanyPrefixes,
      verifiedByGs1Enabled: params.verifiedByGs1Enabled,
      ...(isSuperAdmin && params.tier !== undefined ? { tier: params.tier } : {}),
      ...(isSuperAdmin && params.status !== undefined ? { status: params.status } : {}),
    });
    return res.organisation;
  }

  const orgRef = doc(params.db, 'organisations', params.orgId);
  const existingSnap = await getDoc(orgRef);
  const existingData = existingSnap.exists()
    ? (existingSnap.data() as Partial<OrganisationDocument>)
    : undefined;
  const resolvedTier =
    isSuperAdmin && params.tier !== undefined ? params.tier : (existingData?.tier ?? 'ENTERPRISE');
  const resolvedStatus =
    isSuperAdmin && params.status !== undefined
      ? params.status
      : (existingData?.status ?? 'ACTIVE');

  if (existingSnap.exists()) {
    await updateDoc(orgRef, {
      name: params.name,
      slug: params.slug,
      gcpCompanyPrefixes: params.gcpCompanyPrefixes,
      verifiedByGs1Enabled: params.verifiedByGs1Enabled,
      ...(isSuperAdmin && params.tier !== undefined ? { tier: params.tier } : {}),
      ...(isSuperAdmin && params.status !== undefined ? { status: params.status } : {}),
      updatedAt: serverTimestamp(),
      updatedBy: params.caller.uid,
    });
  } else {
    await setDoc(orgRef, {
      orgId: params.orgId,
      name: params.name,
      slug: params.slug,
      status: resolvedStatus,
      tier: resolvedTier,
      gcpCompanyPrefixes: params.gcpCompanyPrefixes,
      verifiedByGs1Enabled: params.verifiedByGs1Enabled,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: params.caller.uid,
    });
  }

  return {
    orgId: params.orgId,
    name: params.name,
    slug: params.slug,
    status: resolvedStatus,
    tier: resolvedTier,
    gcpCompanyPrefixes: params.gcpCompanyPrefixes,
    verifiedByGs1Enabled: params.verifiedByGs1Enabled,
    createdAt: existingData?.createdAt ?? nowIso,
    updatedAt: nowIso,
    updatedBy: params.caller.uid,
  };
}
